import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import { GtmEventAction, GtmLoginMethod } from "@obg/gtm.types/adaptive";
import {
	AuthenticationType,
	CoreState,
	Jurisdiction,
	JurisdictionSelector,
	SessionSelector,
	SmartIdAuthTransactionAction,
	SmartIdAuthTransactionSelector,
	VerificationStatus,
} from "@obg/ngx.core";
import { TelemetryService } from "@obg/ngx.global.evo";
import { estonianPersonalNumberValidator, latvianPersonalNumberValidator } from "@obg/ngx.ui.common";
import { ignoreNil } from "@obg/rxjs";
import { merge, Subscription } from "rxjs";
import { filter, tap } from "rxjs/operators";

import { CommonAuthTelemetryEventName } from "../../../sdk";
import { SMART_ID_LOGIN_TRANSACTION_PREFIX } from "../smart-id-login.const";
import { SmartIdCountryCode } from "../smart-id-login.model";
import { SmartIdLoginService } from "../smart-id-login.service";

@Component({
	selector: "obg-m-smart-id-login-form-container",
	templateUrl: "./smart-id-login-form.container.html",
	styleUrls: ["./smart-id-login-form.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	providers: [SmartIdLoginService],
})
export class SmartIdLoginFormContainer implements OnInit, OnDestroy {
	@Output() disableTab = new EventEmitter<boolean>();

	personalNumberForm: UntypedFormGroup;
	verification = false;
	isLoading = false;
	showError = false;
	errorMessage = "";

	readonly translationPrefix = SMART_ID_LOGIN_TRANSACTION_PREFIX;
	private data$$: Subscription | undefined;
	private countryCode: string | undefined;

	constructor(
		private store: Store<CoreState>,
		private formBuilder: UntypedFormBuilder,
		private changeDetector: ChangeDetectorRef,
		private smartIdLoginService: SmartIdLoginService,
		private sessionSelector: SessionSelector,
		private smartIdAuthTransactionAction: SmartIdAuthTransactionAction,
		private telemetryService: TelemetryService
	) {
		this.personalNumberForm = this.formBuilder.group({
			personalNumber: this.formBuilder.control(""),
			shouldRememberUser: false,
		});
	}

	ngOnInit() {
		const jurisdiction$ = this.store.pipe(
			select(JurisdictionSelector.get),
			ignoreNil(),
			tap((jurisdiction) => {
				this.countryCode = jurisdiction === Jurisdiction.Mta ? SmartIdCountryCode.EE : SmartIdCountryCode.LV;

				const commonValidators = [Validators.required, Validators.minLength(11), Validators.maxLength(12)];

				const countryValidator =
					jurisdiction === Jurisdiction.Mta
						? estonianPersonalNumberValidator
						: latvianPersonalNumberValidator;

				this.personalNumberForm.controls.personalNumber?.setValidators([...commonValidators, countryValidator]);
			})
		);

		const rememberPersonalNumber$ = this.store
			.select(SmartIdAuthTransactionSelector.getLastActivePersonalNumber)
			.pipe(
				ignoreNil(),
				tap((personalNumber) => {
					this.personalNumberForm.patchValue({
						personalNumber,
						shouldRememberUser: true,
					});
				})
			);

		const customerData$ = this.store.pipe(
			select(this.sessionSelector.getCustomerNotVerifiedData()),
			ignoreNil(),
			tap((data) => {
				const personalNumberField = this.personalNumberForm.controls.personalNumber;
				if (data.personalNumber && !personalNumberField.value) {
					personalNumberField.setValue(data.personalNumber);
					personalNumberField.disable();
				}
			})
		);

		const personalNumberErrors$ = this.smartIdLoginService
			.personalNumberErrors$()
			.pipe(tap((error) => this.displayError(error)));

		const loginTransactionsVerificationStatus$ = this.smartIdLoginService
			.loginTransactionsVerificationStatus$()
			.pipe(
				tap(() => (this.isLoading = false)),
				filter((status) => status !== VerificationStatus.Failed),
				tap((status) => (this.verification = status === VerificationStatus.Pending))
			);

		const cancelLastVerification$ = this.smartIdLoginService
			.cancelLastVerification$()
			.pipe(tap(() => (this.verification = false)));

		const initializeVerificationFail$ = this.smartIdLoginService.initializeVerificationFail$().pipe(
			tap(() => {
				this.displayError("initialize.fail");
				this.verification = false;
			})
		);

		const invalidCredentialsError$ = this.smartIdLoginService.invalidCredentialsError$().pipe(
			tap(() => {
				this.displayError("error.personal-number-not-found");
				this.verification = false;
			})
		);

		const customerVerificationFail$ = this.smartIdLoginService.customerVerificationFail$().pipe(
			tap(() => {
				this.displayError("error.text");
				this.verification = false;
			})
		);

		const customerVerificationCancel$ = this.smartIdLoginService.customerVerificationCancel$().pipe(
			tap(() => {
				this.displayError("error.text");
				this.verification = false;
			})
		);

		const genericLoginError$ = this.smartIdLoginService.genericLoginError$().pipe(
			tap((error) => {
				this.displayError(error || "error.text");
				this.verification = false;
			})
		);

		this.data$$ = merge(
			jurisdiction$,
			customerData$,
			personalNumberErrors$,
			loginTransactionsVerificationStatus$,
			cancelLastVerification$,
			initializeVerificationFail$,
			invalidCredentialsError$,
			customerVerificationFail$,
			customerVerificationCancel$,
			genericLoginError$,
			rememberPersonalNumber$
		).subscribe(() => this.changeDetector.markForCheck());
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();

		if (this.verification) {
			this.store.dispatch(this.smartIdAuthTransactionAction.cancelLastVerification());
		}
	}

	login() {
		this.personalNumberForm.markAllAsTouched();

		if (this.personalNumberForm.invalid) {
			return;
		}

		this.clearError();

		this.isLoading = true;
		this.disableTab.emit(true);
		this.store.dispatch(
			this.smartIdAuthTransactionAction.initializeAuthTransaction({
				personalNumber: this.personalNumberForm.value.personalNumber,
				authenticationType: AuthenticationType.SmartId,
				countryCode: this.countryCode,
				shouldRememberUser: this.personalNumberForm.value.shouldRememberUser,
			})
		);

		this.telemetryService.track({
			LoginMethod: GtmLoginMethod.SmartID,
			event: CommonAuthTelemetryEventName.SmartIDLoginVerificationOpen,
		});
	}

	gtmEventAction(fieldName: string) {
		this.telemetryService.track({
			event: GtmEventAction.LoginFieldChange,
			FieldName: fieldName,
			LoginMethod: GtmLoginMethod.SmartID,
		});
	}

	private displayError(errorMessage: string) {
		this.errorMessage = `${this.translationPrefix}.${errorMessage}`.toLowerCase();
		this.showError = true;
		this.isLoading = false;
		this.disableTab.emit(false);
	}

	private clearError() {
		this.errorMessage = "";
		this.showError = false;
	}
}
